import { AnimatePresence, SizableText, View } from 'tamagui'
import { createContext, useContext, useEffect, useRef, useState } from 'react'

type SnackColor = 'primary' | 'success' | 'danger' | 'warning' | 'neutral' | string

const colorMap: Record<string, string> = {
    primary: '$primary',
    success: '$primary',
    danger: '$red10',
    warning: '$yellow10',
    neutral: '$gray10',
}

function resolveColor(color: SnackColor): string {
    return colorMap[color] ?? color
}

const SnackContext = createContext<{
    showSnack: (text: string, color: SnackColor) => void
}>(undefined as any)

export const useShowSnack = () => useContext(SnackContext).showSnack

export const SnackbarProvider = ({ children }: { children: React.ReactNode }) => {
    const [open, setOpen] = useState(false)
    const [text, setText] = useState('')
    const [color, setColor] = useState<SnackColor>('primary')
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const showSnack = (text: string, color: SnackColor) => {
        setText(text)
        setColor(color)
        setOpen(true)

        if (timerRef.current) {
            clearTimeout(timerRef.current)
        }
        timerRef.current = setTimeout(() => {
            setOpen(false)
        }, 3000)
    }

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
            }
        }
    }, [])

    return (
        <SnackContext.Provider value={{ showSnack }}>
            {children}
            <AnimatePresence>
                {open && (
                    <View
                        key="snackbar"
                        position="absolute"
                        bottom={32}
                        left="50%"
                        x={-175}
                        width={350}
                        zIndex={100_000}
                        backgroundColor={resolveColor(color)}
                        borderRadius="$4"
                        paddingVertical="$3"
                        paddingHorizontal="$5"
                        animation="quick"
                        enterStyle={{ opacity: 0, y: 16 }}
                        exitStyle={{ opacity: 0, y: 16 }}
                        opacity={1}
                        y={0}
                        elevate
                    >
                        <SizableText color="white" textAlign="center" fontWeight="500" size="$3">
                            {text}
                        </SizableText>
                    </View>
                )}
            </AnimatePresence>
        </SnackContext.Provider>
    )
}
